import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { DateField, SelectField } from "../Shared/fieldInputs";
import { Field } from "../Shared/formUi";
import { formatDate } from "../Shared/formatters";
import { loadReprocessDepartments, loadReprocessEmployees, reprocessDailySalary } from "./shiftEarningsApi";

// Add ▾ > From the System: re-runs the daily payroll for a date range so
// shift earnings are rebuilt from attendance. Same fields and API as the
// "Daily Payroll Reprocess" dialog behind main's Reprocess (refresh) icon:
// Department (narrows the employee list), Employee (required here; main
// let it be blank), Start Date and End Date (from the employee's last pay date, up to
// yesterday) -> POST payroll/reprocess_daily_salary/.

const required = (label) => (
  <>
    {label} <Mui.Box component="span" sx={{ color: "#DC2626" }}>*</Mui.Box>
  </>
);

const emptyForm = { departmentId: "", employee: null, startDate: "", endDate: "" };

export const FromSystemDialog = ({ open, onClose, onDone, domain, companyId }) => {
  const { userDetails } = useSelector((state) => state.tokenReducer);
  const [form, setForm] = useState(emptyForm);
  const [showErrors, setShowErrors] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm); setShowErrors(false); setSaveError(""); setLoadError("");
  }, [open]);

  useEffect(() => {
    if (!open || !userDetails?.id) return undefined;
    const controller = new AbortController();
    loadReprocessDepartments(domain, userDetails.id, companyId, controller.signal)
      .then((list) => { if (!controller.signal.aborted) setDepartments(list); })
      .catch((error) => { if (!controller.signal.aborted) setLoadError(error.message); });
    return () => controller.abort();
  }, [open, domain, companyId, userDetails?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setEmployeesLoading(true);
    loadReprocessEmployees(domain, companyId, form.departmentId, controller.signal)
      .then((list) => { if (!controller.signal.aborted) setEmployees(list); })
      .catch((error) => { if (!controller.signal.aborted) { setEmployees([]); setLoadError(error.message); } })
      .finally(() => { if (!controller.signal.aborted) setEmployeesLoading(false); });
    return () => controller.abort();
  }, [open, domain, companyId, form.departmentId]);

  const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
  const earliest = form.employee?.last_pay_date || "";

  const errors = useMemo(() => {
    const result = {};
    if (!form.employee) result.employee = "Employee is required.";
    const checkDate = (key, label) => {
      if (!form[key]) result[key] = `${label} is required.`;
      else if (form[key] > yesterday) result[key] = `${label} can't be after ${formatDate(yesterday)}.`;
      else if (earliest && form[key] < earliest) result[key] = `${label} can't be before the last pay date (${formatDate(earliest)}).`;
    };
    checkDate("startDate", "Start date");
    checkDate("endDate", "End date");
    if (!result.endDate && form.startDate && form.endDate < form.startDate) result.endDate = "End date cannot be before the start date.";
    return result;
  }, [form, yesterday, earliest]);
  const shownError = (key) => (showErrors ? errors[key] : undefined);

  const set = (changes) => setForm((current) => ({ ...current, ...changes }));

  const handleConfirm = async () => {
    if (saving) return;
    if (Object.keys(errors).length) { setShowErrors(true); return; }
    setSaving(true); setSaveError("");
    try {
      await reprocessDailySalary(domain, { startDate: form.startDate, endDate: form.endDate, userId: form.employee?.user_id });
      onDone?.();
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlobalConfirmDialog
      open={open}
      title="Shift Earnings From the System"
      onCancel={saving ? undefined : onClose}
      onConfirm={handleConfirm}
      confirmLabel="Reprocess"
      cancelLabel="Cancel"
      loading={saving}
      loadingLabel="Reprocessing..."
      // md (900px) so Department/Employee and the two dates have room.
      maxWidth="md"
    >
      <Mui.Stack spacing={2}>
        <Mui.Typography sx={{ fontSize: 14 }}>
          Reprocess the daily payroll for the selected dates. Shift earnings are rebuilt from attendance.
        </Mui.Typography>
        {loadError ? <Mui.Alert severity="warning">{loadError}</Mui.Alert> : null}
        <Field label="Department" helper="Narrows the employee list.">
          <SelectField
            value={form.departmentId}
            onChange={(departmentId) => set({ departmentId, employee: null })}
            options={[{ value: "", label: "All" }, ...departments]}
            placeholder="All"
            disabled={saving}
          />
        </Field>
        <Field label={required("Employee")} helper="Employee whose daily payroll is reprocessed." error={shownError("employee")}>
          <CustomAutoCompleteWithLogo
            dataList={employees}
            selectedValue={form.employee}
            updateDataFunction={(event, option) => set({ employee: option || null })}
            placeHolder={employeesLoading ? "Loading employees..." : "Select employee"}
            textBoxError={Boolean(shownError("employee"))}
            disableAction={saving || employeesLoading}
            showAvathar={!form.employee}
          />
        </Field>
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <Field label={required("Start Date")} helper={earliest ? `From ${formatDate(earliest)}` : "Up to yesterday."} error={shownError("startDate")}>
            <DateField value={form.startDate} onChange={(startDate) => set({ startDate })} minDate={earliest} maxDate={form.endDate || yesterday} error={shownError("startDate")} disabled={saving} />
          </Field>
          <Field label={required("End Date")} helper={`Up to ${formatDate(yesterday)}`} error={shownError("endDate")}>
            <DateField value={form.endDate} onChange={(endDate) => set({ endDate })} minDate={form.startDate || earliest} maxDate={yesterday} error={shownError("endDate")} disabled={saving} />
          </Field>
        </Mui.Box>
        {saveError ? <RequestInfoBanner tone="danger" title="Could not reprocess" body={saveError} /> : null}
      </Mui.Stack>
    </GlobalConfirmDialog>
  );
};
